/**
 * Main App Component
 * ConnectWise Pod for Copilot Studio Integration
 *
 * Authentication is deferred until user clicks "Analyze Ticket"
 * UI loads immediately without waiting for auth
 */

import { useAuth } from './hooks/useAuth';
import { useConnectWise } from './hooks/useConnectWise';
import { useCopilot } from './hooks/useCopilot';
import TicketSummary from './components/TicketSummary';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorDisplay from './components/ErrorDisplay';
import './App.css';

function App() {
  // Authentication - now with login() for on-demand auth
  const { accessToken, loading: authLoading, error: authError, isAuthenticated, login } = useAuth();

  // ConnectWise integration
  const {
    ticketId,
    screenType,
    ticketData,
    loading: cwLoading,
    error: cwError,
    refreshTicket,
    connectWiseUser
  } = useConnectWise();

  // Copilot integration - pass login function for on-demand auth and ConnectWise user info
  const {
    messages,
    loading: copilotLoading,
    error: copilotError,
    hasHistory,
    analyzeTicket,
    sendFollowUp,
    startFreshConversation
  } = useCopilot(accessToken, ticketId, login, connectWiseUser);

  // NOTE: Auth loading/error no longer blocks UI
  // Authentication happens on-demand when user clicks "Analyze Ticket"

  // Show loading state while fetching ticket from ConnectWise
  if (cwLoading) {
    return (
      <div className="app">
        <LoadingSpinner message="Loading ticket information..." />
      </div>
    );
  }

  // Show ConnectWise error
  if (cwError) {
    return (
      <div className="app">
        <ErrorDisplay
          error={cwError}
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  // Not on a ticket screen
  if (!ticketData || screenType !== 'ticket') {
    return (
      <div className="app">
        <div className="info-message">
          <h2>Natalie</h2>
          <p>This pod is only available on ticket screens.</p>
          {screenType && (
            <p className="screen-info">Current screen: <strong>{screenType}</strong></p>
          )}
        </div>
      </div>
    );
  }

  // Handle analyze ticket (initial or continue)
  const handleAnalyze = async () => {
    await refreshTicket();
    if (ticketData) {
      await analyzeTicket(ticketData);
    }
  };

  // Handle follow-up questions
  const handleFollowUp = async (message, type = 'follow-up') => {
    return await sendFollowUp(message, type);
  };

  // Handle new conversation (clear history)
  const handleNewConversation = () => {
    startFreshConversation();
  };

  return (
    <div className="app">
      {/* Header */}
      <header className="app-header">
        <img src="/company-logo.png" alt="Company Logo" className="header-logo" />
        {isAuthenticated && (
          <div className="auth-status">
            <span className="status-indicator"></span>
            Connected
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="app-content">
        <TicketSummary
          ticketData={ticketData}
          messages={messages}
          hasHistory={hasHistory}
          loading={copilotLoading || authLoading}
          onAnalyze={handleAnalyze}
          onFollowUp={handleFollowUp}
          onNewConversation={handleNewConversation}
        />

        {/* Show Auth Error inline (not blocking) */}
        {authError && (
          <div className="error-banner">
            Authentication Error: {authError}
          </div>
        )}

        {/* Show Copilot Error (but keep ticket visible) */}
        {copilotError && (
          <div className="error-banner">
            AI Analysis Error: {copilotError}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="app-footer">
        <span className="footer-text">Ticket #{ticketId}</span>
        <span className="footer-text">|</span>
        <span className="footer-text">Powered by Akka</span>
      </footer>
    </div>
  );
}

export default App;
